import { FC, useCallback, useMemo } from "react";
import { Box } from "@otrium/core";
import { Trans } from "@lingui/macro";
import { Checkbox } from "src/atoms/Checkbox";
import { useFilterApplied } from "src/organisms/AlgoliaProductList/useFilterApplied";
import { ShowResults } from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/ShowResults";
import { HitType } from "src/types/Algolia";
import { OutsideClickableWrapper } from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { GoodOnYouLabel, RateLabelWrapper } from "./ConsciousFilter.styled";
import FaceContentIcon from "src/icons/FaceContentIcon";
import FaceSmileIcon from "src/icons/FaceSmileIcon";
import FaceHappyIcon from "src/icons/FaceHappyIcon";

interface Props {
  items: HitType[];
  refine: (value: string[]) => void;
  outerRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}

const ConsciousFilter: FC<Props> = ({ items, refine, onClose, outerRef }) => {
  const { filterApplied, handleRefine } = useFilterApplied(items, refine);
  const isEveryItemSelected = useMemo(
    () => items.every((item) => item.isRefined),
    [items]
  );

  const toggleSelectAll = useCallback(() => {
    const itemsToRefine = isEveryItemSelected
      ? []
      : items.reduce((previousValue, currentValue) => {
          previousValue.push(currentValue.label);
          return previousValue;
        }, [] as string[]);
    handleRefine(itemsToRefine)();
  }, [handleRefine, isEveryItemSelected, items]);

  return (
    <OutsideClickableWrapper handler={onClose} outerRef={outerRef}>
      <Box
        bg="tone.sand"
        py={[4, 4, 3]}
        pl={3}
        ml={[0, 0, 0, "-16px"]}
        mb={3}
        width="calc(100% + 32px)"
      >
        <Checkbox checked={isEveryItemSelected} onChange={toggleSelectAll}>
          <span data-testid="conscious-filter-select-all">
            <Trans>Select all</Trans>
          </span>
        </Checkbox>
      </Box>
      {items?.map((item, i) => {
        const label = item.label.split("||");
        const labelRate = label[0];
        const labelName = label[1];
        return (
          <Box key={`size-filter-item-${i}`} mb={3}>
            <Checkbox
              checked={item.isRefined}
              onChange={handleRefine(item.value)}
              style={{ display: "flex", alignItems: "center" }}
            >
              <RateLabelWrapper>
                {labelRate === "3" && (
                  <FaceContentIcon width={16} height={16} stroke="black" />
                )}
                {labelRate === "4" && (
                  <FaceSmileIcon width={16} height={16} stroke="black" />
                )}
                {labelRate === "5" && (
                  <FaceHappyIcon width={16} height={16} stroke="black" />
                )}
                {labelRate}/5 {labelName}
              </RateLabelWrapper>
            </Checkbox>
          </Box>
        );
      })}
      <GoodOnYouLabel>
        <Trans>Provided by Good On You</Trans>®.{" "}
        <a
          data-testid="good-on-you-link"
          href={`http://howwerateourbrands.otrium.com/`}
        >
          <Trans>Learn how this is rated</Trans>
        </a>
      </GoodOnYouLabel>
      <ShowResults hasFilters={filterApplied} onClick={onClose} />
    </OutsideClickableWrapper>
  );
};

export default ConsciousFilter;
